import { useCallback, useEffect, useRef, useState } from 'react';
import { CheckIcon, EditIcon, SaveIcon, WarnIcon } from '../../components/icons';
import {
  getApiErrorInfo,
  getProject,
  patchProject,
  submitProject,
  type ApiErrorInfo,
  type ProjectDetails,
  type ProjectPatch,
} from '../../lib/api';
import { track } from '../../lib/analytics';
import { isProjectStageValue, normalizeProjectStageValue } from '../../lib/projectMeta';
import { useBackButton } from '../../hooks/useBackButton';
import { useClosingConfirmation } from '../../hooks/useClosingConfirmation';
import { useDisableSwipes } from '../../hooks/useSwipeBehavior';
import { useMainButton } from '../../hooks/useMainButton';
import { useDraft } from '../../hooks/useDraft';
import { DraftBanner } from '../../components/DraftBanner';
import { cloud } from '../../utils/cloudStorage';
import { haptic } from '../../utils/haptic';
import { WizardStep1 } from './WizardStep1';
import { WizardStep2 } from './WizardStep2';
import { WizardStep3 } from './WizardStep3';
import { WizardStep4 } from './WizardStep4';

const TABS = [
  { step: 1, label: '1. Основное' },
  { step: 2, label: '2. Суть' },
  { step: 3, label: '3. Стек' },
  { step: 4, label: '4. Условия' },
] as const;

/** Map required field key → wizard step (for routing on submit). */
const FIELD_TO_STEP: Record<string, number> = {
  project_title: 1,
  niche: 1,
  problem: 2,
  what_done: 2,
  audience_type: 2,
  project_status: 4,
  dev_time: 4,
};

const FIELD_LABELS: Record<string, string> = {
  project_title: 'название',
  niche: 'нишу',
  problem: 'проблему',
  what_done: 'что сделано',
  audience_type: 'целевую аудиторию',
  project_status: 'стадию проекта',
  dev_time: 'срок разработки',
};

const AUTOSAVE_MS = 1000;

function getDraftFromAnswers(answers: Record<string, unknown> | null): WizardDraft {
  const a = answers ?? {};
  const str = (k: string) => (typeof a[k] === 'string' ? (a[k] as string) : '');
  // Normalize array items: if item is an object with a `value` field, extract it as string
  const arr = (k: string): string[] => {
    if (!Array.isArray(a[k])) return str(k) ? [str(k)] : [];
    return (a[k] as unknown[]).map((item) => {
      if (typeof item === 'string') return item;
      if (item !== null && typeof item === 'object' && 'value' in (item as object)) {
        return String((item as Record<string, unknown>).value);
      }
      return String(item);
    });
  };
  const audienceType = str('audience_type');
  const audienceList = audienceType ? audienceType.split(',').map((s) => s.trim()).filter(Boolean) : [];
  const rawStatuses = arr('statuses');
  const inferredProjectStatus = str('project_status') || rawStatuses.find((value) => isProjectStageValue(value)) || '';
  const statuses = rawStatuses.filter((value, index, list) => (
    !isProjectStageValue(value) && list.indexOf(value) === index
  ));
  return {
    emoji: str('emoji') || 'bot',
    project_title: str('project_title'),
    category: str('category'),
    niche_type: str('niche_type') || str('niche'),
    problem: str('problem'),
    what_done: str('what_done'),
    potential: str('potential'),
    audience: Array.isArray(a.audience) ? (a.audience as string[]) : audienceList,
    tools: arr('tools').length ? arr('tools') : (a.tools as string[] | undefined) ?? [],
    project_status: normalizeProjectStageValue(inferredProjectStatus),
    statuses,
    dev_time: str('dev_time'),
    price: str('price'),
    show_contacts: a.show_contacts === true,
    site_url: str('site_url') || str('website_url'),
    demo_url: str('demo_url'),
    github_url: str('github_url') || str('repo_url'),
    tg_url: str('tg_url'),
  };
}

function draftToPatch(d: WizardDraft): ProjectPatch {
  return {
    emoji: d.emoji || null,
    project_title: d.project_title || null,
    category: d.category || null,
    niche: d.niche_type || null,
    problem: d.problem || null,
    what_done: d.what_done || null,
    potential: d.potential || null,
    audience_type: d.audience.length ? d.audience.join(', ') : null,
    tools: d.tools.length ? d.tools : null,
    statuses: d.statuses.length ? d.statuses : null,
    project_status: d.project_status || null,
    dev_time: d.dev_time || null,
    price: d.price || null,
    site_url: d.site_url || null,
    demo_url: d.demo_url || null,
    github_url: d.github_url || null,
    tg_url: d.tg_url || null,
  };
}

export interface WizardDraft {
  emoji: string;
  project_title: string;
  category: string;
  niche_type: string;
  problem: string;
  what_done: string;
  potential: string;
  audience: string[];
  tools: string[];
  project_status: string;
  statuses: string[];
  dev_time: string;
  price: string;
  show_contacts: boolean;
  site_url: string;
  demo_url: string;
  github_url: string;
  tg_url: string;
}

const EMPTY_WIZARD_DRAFT: WizardDraft = {
  emoji: '',
  project_title: '',
  category: '',
  niche_type: '',
  problem: '',
  what_done: '',
  potential: '',
  audience: [],
  tools: [],
  project_status: '',
  statuses: [],
  dev_time: '',
  price: '',
  show_contacts: false,
  site_url: '',
  demo_url: '',
  github_url: '',
  tg_url: '',
};

export function WizardScreen({
  projectId,
  onBack,
  onOpenDetails,
  onAfterSave,
}: {
  projectId: string;
  onBack: () => void;
  onOpenDetails: (id: string, source?: 'my' | 'public') => void;
  onAfterSave?: () => Promise<void> | void;
}) {
  const [details, setDetails] = useState<ProjectDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [publishing, setPublishing] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [, setErrorInfo] = useState<ApiErrorInfo | null>(null);
  const [autosaveStatus, setAutosaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [step, setStep] = useState(1);
  const [draft, setDraft] = useState<WizardDraft>(() => getDraftFromAnswers(null));
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const cloudDraft = useDraft<WizardDraft>({
    key: 'draft_project',
    initialValue: EMPTY_WIZARD_DRAFT,
    saveInterval: 5000,
  });
  const latestDraftRef = useRef(draft);
  latestDraftRef.current = draft;
  const detailsRef = useRef(details);
  detailsRef.current = details;
  const publishedTrackedRef = useRef(false);

  const handleBack = useCallback(() => {
    if (step > 1) setStep((s) => s - 1);
    else onBack();
  }, [step, onBack]);
  useBackButton(handleBack, true);
  useClosingConfirmation(true);
  useDisableSwipes(true);

  const load = useCallback(async () => {
    setLoading(true);
    setErrorMessage(null);
    setErrorInfo(null);
    try {
      const data = await getProject(projectId);
      setDetails(data);
      const base = getDraftFromAnswers(data.answers as Record<string, unknown>);
      setDraft({
        ...base,
        show_contacts: Boolean((data as { show_contacts?: boolean }).show_contacts),
      });
    } catch (err) {
      const info = getApiErrorInfo(err);
      setErrorInfo(info);
      setErrorMessage(info.message || 'Не удалось загрузить проект');
      setDetails(null);
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!loading && details) track('wizard_started');
  }, [loading, details]);

  useEffect(() => {
    if (!loading && details && step) track('wizard_step_completed', { step });
  }, [loading, details, step]);

  useEffect(() => {
    if (details?.status === 'published' && !publishedTrackedRef.current) {
      publishedTrackedRef.current = true;
      track('project_published');
    }
  }, [details?.status]);

  const doAutosave = useCallback(async () => {
    const d = latestDraftRef.current;
    const prev = detailsRef.current?.answers as Record<string, unknown> | undefined;
    const prevDraft = prev ? getDraftFromAnswers(prev) : null;
    const patch = draftToPatch(d);
    const changed: ProjectPatch = {};
    if (prevDraft) {
      if (patch.emoji !== prevDraft.emoji) changed.emoji = patch.emoji ?? undefined;
      if (patch.project_title !== prevDraft.project_title) changed.project_title = patch.project_title ?? undefined;
      if (patch.category !== prevDraft.category) changed.category = patch.category ?? undefined;
      if (patch.niche !== prevDraft.niche_type) changed.niche = patch.niche ?? undefined;
      if (patch.problem !== prevDraft.problem) changed.problem = patch.problem ?? undefined;
      if (patch.what_done !== prevDraft.what_done) changed.what_done = patch.what_done ?? undefined;
      if (patch.potential !== prevDraft.potential) changed.potential = patch.potential ?? undefined;
      if ((patch.audience_type ?? '') !== (prevDraft.audience.join(', ') ?? '')) changed.audience_type = patch.audience_type ?? undefined;
      const sameTools =
        JSON.stringify(patch.tools ?? []) === JSON.stringify(prevDraft.tools ?? []);
      if (!sameTools) changed.tools = patch.tools ?? undefined;
      const sameStatuses =
        JSON.stringify(patch.statuses ?? []) === JSON.stringify(prevDraft.statuses ?? []);
      if (!sameStatuses) changed.statuses = patch.statuses ?? undefined;
      if (patch.project_status !== prevDraft.project_status) changed.project_status = patch.project_status ?? undefined;
      if (patch.dev_time !== prevDraft.dev_time) changed.dev_time = patch.dev_time ?? undefined;
      if (patch.price !== prevDraft.price) changed.price = patch.price ?? undefined;
      if (patch.site_url !== prevDraft.site_url) changed.site_url = patch.site_url ?? undefined;
      if (patch.demo_url !== prevDraft.demo_url) changed.demo_url = patch.demo_url ?? undefined;
      if (patch.github_url !== prevDraft.github_url) changed.github_url = patch.github_url ?? undefined;
      if (patch.tg_url !== prevDraft.tg_url) changed.tg_url = patch.tg_url ?? undefined;
    } else {
      Object.assign(changed, patch);
    }
    if (Object.keys(changed).length === 0) return;
    setAutosaveStatus('saving');
    try {
      await patchProject(projectId, changed);
      const data = await getProject(projectId);
      setDetails(data);
      setAutosaveStatus('saved');
      setTimeout(() => setAutosaveStatus((s) => (s === 'saved' ? 'idle' : s)), 2000);
    } catch {
      setAutosaveStatus('error');
      setTimeout(() => setAutosaveStatus((s) => (s === 'error' ? 'idle' : s)), 3000);
    }
  }, [projectId]);

  const scheduleAutosave = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(doAutosave, AUTOSAVE_MS);
  }, [doAutosave]);

  const onPatch = useCallback(
    (patch: Partial<WizardDraft>) => {
      setDraft((prev) => ({ ...prev, ...patch }));
      scheduleAutosave();
    },
    [scheduleAutosave],
  );

  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  useEffect(() => {
    if (!details || loading) return;
    const id = setInterval(() => {
      cloud.set('draft_project', latestDraftRef.current);
    }, 5000);
    return () => clearInterval(id);
  }, [details, loading]);

  const doSubmit = useCallback(async () => {
    if (publishing || !details) return;
    const canSubmit =
      details.status === 'draft' || details.status === 'rejected' || details.status === 'needs_fix';
    if (!canSubmit) return;
    if (timerRef.current) clearTimeout(timerRef.current);
    setPublishing(true);
    setErrorMessage(null);
    setErrorInfo(null);
    try {
      const patch = draftToPatch(latestDraftRef.current);
      await patchProject(projectId, patch);
      const fresh = await getProject(projectId);
      if (fresh.missing_fields?.length) {
        const missing = fresh.missing_fields;
        const keys = missing.map((k) => (typeof k === 'string' ? k : (k as { field?: string })?.field ?? String(k)));
        const steps = keys.map((k) => FIELD_TO_STEP[k]).filter((s) => s != null) as number[];
        const stepNum = steps.length ? Math.min(...steps) : 4;
        setStep(stepNum);
        const list = keys.slice(0, 5).map((k) => FIELD_LABELS[k] || k).join(', ');
        const stepLabel = TABS.find((t) => t.step === stepNum)?.label ?? `шаг ${stepNum}`;
        haptic.error();
        setErrorMessage(`Заполните обязательные поля (${stepLabel}): ${list}${keys.length > 5 ? '…' : ''}`);
        setPublishing(false);
        return;
      }
      const res = await submitProject(projectId, { show_contacts: latestDraftRef.current.show_contacts });
      setDetails(res);
      haptic.success();
      track('project_submitted');
      cloudDraft.clearDraft();
      await onAfterSave?.();
    } catch (err) {
      const info = getApiErrorInfo(err);
      setErrorInfo(info);
      const raw = typeof info.message === 'string' ? info.message : String(info.message || '');
      const msg =
        info.status === 429
          ? 'Вы уже публиковали проект недавно. Подождите 10 минут.'
          : raw.includes('Missing required')
            ? 'Заполните все обязательные поля в форме и попробуйте снова.'
            : raw || 'Не удалось отправить на модерацию';
      haptic.error();
      setErrorMessage(msg);
    } finally {
      setPublishing(false);
    }
  }, [details, onAfterSave, projectId, publishing]);

  const isLastStep = step === 4;

  const mainButtonVisible =
    !loading && !!details && details.status !== 'submitted' && details.status !== 'approved' && details.status !== 'published';
  const step1Valid = draft.project_title.trim().length > 0 && draft.category.length > 0;
  const step2Valid =
    draft.problem.trim().length > 0 &&
    draft.what_done.trim().length > 0 &&
    draft.audience.length > 0;
  const step3Valid = draft.tools.length > 0;
  const step4Valid = draft.project_status.trim().length > 0 && draft.dev_time.trim().length > 0;

  useMainButton({
    text: isLastStep ? 'На модерацию' : 'Далее →',
    onClick: isLastStep
      ? () => doSubmit()
      : () => {
          haptic.tap();
          setStep((s) => s + 1);
        },
    visible: mainButtonVisible,
    disabled: isLastStep
      ? !step4Valid || publishing
      : step === 1
        ? !step1Valid
        : step === 2
          ? !step2Valid
          : step === 3
            ? !step3Valid
            : !step4Valid,
    loading: isLastStep && publishing,
  });

  if (loading) {
    return (
      <div className="wizard-screen showcase-screen" style={{ padding: 16 }}>
        <div className="loading" style={{ padding: '2rem 0' }}>
          <div className="spinner" />
          <p style={{ marginTop: 8, color: 'rgba(255,255,255,0.6)' }}>Загрузка...</p>
        </div>
      </div>
    );
  }

  if (!details) {
    return (
      <div className="wizard-screen showcase-screen" style={{ padding: 16 }}>
        <p style={{ color: '#f87171', marginTop: 12 }}>{errorMessage}</p>
        <button type="button" className="btn btn-primary" onClick={load} style={{ marginTop: 12 }}>
          Повторить
        </button>
      </div>
    );
  }

  const submitted = details.status === 'submitted' || details.status === 'approved' || details.status === 'published';
  if (submitted) {
    return (
      <div className="wizard-screen showcase-screen" style={{ padding: 16, paddingBottom: 80 }}>
        <div className="card" style={{ marginTop: 12, background: 'rgba(255,255,255,0.045)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 16, padding: 16 }}>
          <h3 style={{ fontFamily: 'Unbounded, sans-serif', fontSize: '1rem', marginBottom: 8, color: '#fff' }}>
            {details.status === 'submitted'
              ? 'Проект на модерации'
              : details.status === 'published'
                ? 'Проект опубликован!'
                : 'Ожидает публикации'}
          </h3>
          <p style={{ fontSize: '0.9rem', color: 'rgba(255,255,255,0.7)' }}>
            {details.status === 'submitted'
              ? 'Ожидайте решения модератора.'
              : details.status === 'published'
                ? 'Проект уже в канале.'
                : 'Скоро появится в канале.'}
          </p>
          <button
            type="button"
            className="btn btn-secondary"
            style={{ marginTop: 12 }}
            onClick={() => onOpenDetails(projectId, 'my')}
          >
            Открыть проект
          </button>
        </div>
      </div>
    );
  }

  const fixRequest =
    details.status === 'needs_fix' && (details as unknown as { fix_request?: string }).fix_request
      ? String((details as unknown as { fix_request: string }).fix_request)
      : null;

  return (
    <div className="wizard-screen showcase-screen" style={{ padding: 16, paddingBottom: 24 }}>
      {/* Segmented stepper */}
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 20 }}>
        {TABS.map((t, idx) => {
          const done = step > t.step;
          const active = step === t.step;
          return (
            <div key={t.step} style={{ display: 'flex', alignItems: 'center', flex: idx < TABS.length - 1 ? 1 : 'none' }}>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                <button
                  type="button"
                  onClick={() => { haptic.select(); setStep(t.step); }}
                  style={{
                    width: 28,
                    height: 28,
                    borderRadius: '50%',
                    border: active ? '2px solid #7850ff' : done ? 'none' : '1px solid rgba(255,255,255,0.12)',
                    background: done ? 'linear-gradient(135deg,#7850ff,#c084fc)' : active ? 'rgba(120,80,255,0.15)' : 'rgba(255,255,255,0.04)',
                    color: done ? '#fff' : active ? '#c084fc' : 'rgba(255,255,255,0.3)',
                    fontSize: '0.6875rem',
                    fontWeight: 700,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transform: active ? 'scale(1.1)' : 'scale(1)',
                    transition: 'all 0.2s ease',
                    flexShrink: 0,
                  }}
                >
                  {done ? '✓' : t.step}
                </button>
                <span style={{
                  fontSize: '0.625rem',
                  color: done || active ? '#c084fc' : 'rgba(255,255,255,0.25)',
                  whiteSpace: 'nowrap',
                  fontWeight: active ? 600 : 400,
                  transition: 'color 0.2s',
                }}>
                  {t.label.replace(/^\d+\.\s*/, '')}
                </span>
              </div>
              {idx < TABS.length - 1 && (
                <div style={{
                  flex: 1,
                  height: 2,
                  margin: '0 6px',
                  marginBottom: 16,
                  background: 'rgba(255,255,255,0.07)',
                  borderRadius: 1,
                  overflow: 'hidden',
                }}>
                  <div style={{
                    height: '100%',
                    width: done ? '100%' : active ? '50%' : '0%',
                    background: 'linear-gradient(90deg,#7850ff,#c084fc)',
                    borderRadius: 1,
                    transition: 'width 0.35s ease',
                  }} />
                </div>
              )}
            </div>
          );
        })}
      </div>
      <p style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.4)', marginBottom: 8 }}>
        Изменения сохраняются автоматически.
      </p>
      {autosaveStatus !== 'idle' && (
        <p style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.4)', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 4 }}>
          {autosaveStatus === 'saving' && <><SaveIcon size={11} /> Сохранение...</>}
          {autosaveStatus === 'saved' && <><CheckIcon size={11} /> Сохранено</>}
          {autosaveStatus === 'error' && <><WarnIcon size={11} color="rgba(255,255,255,0.4)" /> Ошибка сохранения</>}
        </p>
      )}
      {cloudDraft.hasDraft && cloudDraft.savedDraft && (
        <DraftBanner
          onRestore={() => {
            const restoredDraft = getDraftFromAnswers(cloudDraft.savedDraft as unknown as Record<string, unknown>);
            setDraft({
              ...EMPTY_WIZARD_DRAFT,
              ...restoredDraft,
              show_contacts: Boolean((cloudDraft.savedDraft as Partial<WizardDraft>).show_contacts),
            });
            cloudDraft.discardDraft();
          }}
          onDiscard={cloudDraft.discardDraft}
        />
      )}
      {errorMessage && (
        <p style={{ fontSize: '0.875rem', color: 'var(--app-text-destructive)', marginBottom: 8 }}>{errorMessage}</p>
      )}
      {fixRequest && (
        <div
          style={{
            padding: 10,
            borderRadius: 8,
            background: 'rgba(251,191,36,0.1)',
            border: '1px solid rgba(251,191,36,0.3)',
            marginBottom: 12,
            fontSize: '0.875rem',
          }}
        >
          <EditIcon size={12} style={{ marginRight: 4, verticalAlign: 'middle' }} /> <strong>Комментарий модератора:</strong> {fixRequest}
        </div>
      )}

      {step === 1 && (
        <WizardStep1
          data={{
            emoji: draft.emoji,
            project_title: draft.project_title,
            category: draft.category,
            niche_type: draft.niche_type,
          }}
          onPatch={(p) => onPatch(p)}
        />
      )}
      {step === 2 && (
        <WizardStep2
          data={{
            problem: draft.problem,
            what_done: draft.what_done,
            audience: draft.audience,
          }}
          onPatch={(p) => onPatch(p)}
        />
      )}
      {step === 3 && (
        <WizardStep3 data={{ tools: draft.tools }} onPatch={(p) => onPatch(p)} />
      )}
      {step === 4 && (
        <WizardStep4
          data={{
            project_status: draft.project_status,
            statuses: draft.statuses,
            dev_time: draft.dev_time,
            price: draft.price,
            potential: draft.potential,
            show_contacts: draft.show_contacts,
            site_url: draft.site_url,
            demo_url: draft.demo_url,
            github_url: draft.github_url,
            tg_url: draft.tg_url,
          }}
          onPatch={(p) => onPatch(p)}
        />
      )}
    </div>
  );
}
